/**
 * All Sports List Component
 * Displays list of all sports with icons, names, and event counts
 * Shows when "All Sports" is selected
 */

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useScreenSize } from '@/hooks/useScreenSize';
import {
  fetchAllInPlay,
  fetchAllMatches,
  fetchMatchesToday,
  fetchMatchesTomorrow,
  collectMarkets,
} from '@/services/sports/marketApiService';
import SportsSVGIcons from '@/components/sports/SportsSVGIcons';
import styles from './AllSportsList.module.css';

// Sport configuration with IDs and display names (matching SportsTabs)
const SPORTS_CONFIG = [
  { id: 'cricket', name: 'Cricket', sportId: '4', icon: 'cricket' },
  { id: 'soccer', name: 'Soccer', sportId: '1', icon: 'soccer' },
  { id: 'tennis', name: 'Tennis', sportId: '2', icon: 'tennis' },
  { id: 'mma', name: 'Mixed Martial Arts', sportId: '26420387', icon: null },
  { id: 'golf', name: 'Golf', sportId: '3', icon: null },
  { id: 'boxing', name: 'Boxing', sportId: '6', icon: null },
  { id: 'american-football', name: 'American Football', sportId: '6423', icon: null },
  { id: 'baseball', name: 'Baseball', sportId: '7511', icon: null },
  { id: 'basketball', name: 'Basketball', sportId: '7522', icon: 'basketball' },
  { id: 'rugby-league', name: 'Rugby League', sportId: '1477', icon: null },
  { id: 'rugby-union', name: 'Rugby Union', sportId: '5', icon: null },
  { id: 'horse-racing', name: 'Horse Racing', sportId: '7', icon: null },
  { id: 'greyhound-racing', name: 'Greyhound Racing', sportId: '4339', icon: null },
  { id: 'motorsport', name: 'Motor Sport', sportId: '8', icon: null },
  { id: 'snooker', name: 'Snooker', sportId: '6422', icon: 'snooker' },
  { id: 'darts', name: 'Darts', sportId: '3503', icon: 'darts' },
  { id: 'gaelic-games', name: 'Gaelic Games', sportId: '2152880', icon: null },
  { id: 'ice-hockey', name: 'Ice Hockey', sportId: '7512', icon: 'ice-hockey' },
];

const AllSportsList = ({ currentTab = 'in-play' }) => {
  const navigate = useNavigate();
  const { isMobile } = useScreenSize();
  const [sortBy, setSortBy] = useState('popularity'); // 'popularity' or 'alphabetical'

  // Fetch all markets to calculate event counts
  const { data: sportsWithCounts, isLoading } = useQuery({
    queryKey: ['allSportsCounts', currentTab],
    queryFn: async () => {
      let allMarkets = [];

      if (currentTab === 'in-play') {
        const [inPlayData, allMatchesData] = await Promise.all([
          fetchAllInPlay(),
          fetchAllMatches(),
        ]);

        const inPlayMarkets = collectMarkets(inPlayData).map((m) => ({
          ...m,
          inplay: true,
        }));
        const allMarketsData = collectMarkets(allMatchesData);

        // Merge with in-play preferred
        const map = new Map();
        inPlayMarkets.forEach((m) => m?.marketId && map.set(m.marketId, m));
        allMarketsData.forEach((m) => {
          if (m?.marketId && !map.has(m.marketId)) map.set(m.marketId, m);
        });
        allMarkets = Array.from(map.values());
      } else if (currentTab === 'today') {
        const data = await fetchMatchesToday();
        allMarkets = collectMarkets(data);
      } else if (currentTab === 'tomorrow') {
        const data = await fetchMatchesTomorrow();
        allMarkets = collectMarkets(data);
      }

      // Count markets per sport
      const sportCounts = new Map();
      allMarkets.forEach((market) => {
        const sportId = market.sportId || market.gameId;
        if (sportId) {
          sportCounts.set(sportId, (sportCounts.get(sportId) || 0) + 1);
        }
      });

      // Map sports with counts
      return SPORTS_CONFIG.map((sport) => ({
        ...sport,
        count: sportCounts.get(sport.sportId) || 0,
      }));
    },
    staleTime: 15 * 1000, // 15 seconds
    refetchInterval: 30 * 1000, // Refetch every 30 seconds
    refetchIntervalInBackground: false, // Only refetch when tab is active
    retry: 2,
    retryDelay: 1000,
  });

  const handleSportClick = (sport) => {
    navigate(`/sports/${currentTab}/${sport.id}`);
  };

  // Sort sports
  const sortedSports = React.useMemo(() => {
    if (!sportsWithCounts) return [];

    if (sortBy === 'popularity') {
      return [...sportsWithCounts].sort((a, b) => b.count - a.count);
    } else {
      return [...sportsWithCounts].sort((a, b) => a.name.localeCompare(b.name));
    }
  }, [sportsWithCounts, sortBy]);

  // Only show loader on initial load (when no data exists yet)
  if (isLoading && !sportsWithCounts) {
    return (
      <div className={styles.allSportsList}>
        <div className={styles.loadingContainer}>
          <div className={styles.loader}>
            <span className={styles.loaderText}>loading</span>
            <span className={styles.load}></span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.allSportsList}>
      {/* Sort Tabs - Only show Popularity (hide A-Z as per user request) */}
      <div className={styles.sortTabs}>
        {/* <button
          className={`${styles.sortTab} ${sortBy === 'popularity' ? styles.active : ''}`}
          onClick={() => setSortBy('popularity')}
        >
          Popularity
        </button> */}
        {/* A-Z tab hidden as per user request */}
      </div>

      {/* Sports List */}
      <div className={styles.sportsList}>
        {sortedSports.map((sport) => (
          <div
            key={sport.id}
            className={styles.sportItem}
            onClick={() => handleSportClick(sport)}
          >
            <div className={styles.sportIcon}>
              {sport.icon ? (
                <SportsSVGIcons
                  sportId={sport.icon}
                  size={isMobile ? '24px' : '32px'}
                />
              ) : (
                <SportsSVGIcons
                  sportId={sport.id}
                  size={isMobile ? '24px' : '32px'}
                />
              )}
            </div>
            <div className={styles.sportInfo}>
              <span className={styles.sportName}>{sport.name}</span>
              <span className={styles.sportCount}>{sport.count}</span>
            </div>
            <div className={styles.chevron}>›</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AllSportsList;
